<template>
  <section :class="prefixCls" class="flex justify-center items-center">
    <article class="flex items-center">
      <SvgIcon :name="`sys-${statusRes}`" size="300" />
      <div class="infos ml-8">
        <h1 class="title">{{ title }}</h1>
        <p class="desc my-4">{{ subTitle }}</p>
        <div class="actions">
          <router-link :to="homePath">
            <Button class="btn" type="primary">{{ btnText }}</Button>
          </router-link>
        </div>
      </div>
    </article>
  </section>
</template>

<script setup lang="ts">
  import { useDesign } from '/@/hooks/web/useDesign';
  import { useI18n } from '@mcex/locale';
  import { SvgIcon } from '/@/components/Icon';
  import { computed, ref, unref } from 'vue';
  import { ExceptionEnum } from '/@/enums/exceptionEnum';
  import { useRoute } from 'vue-router';
  import { PackageEnum } from '/@/enums/pageEnum';
  interface MapValue {
    title: string;
    subTitle: string;
    btnText?: string;
    icon?: string;
    handler?: Fn;
    status?: string;
  }

  const { t } = useI18n();
  const { prefixCls } = useDesign('main-box');

  const props = defineProps({
    status: {
      type: Number as PropType<number>,
      default: ExceptionEnum.PAGE_NOT_ACCESS,
    },
  });
  const statusMapRef = ref(new Map<string | number, MapValue>());
  const route = useRoute();
  const { query, path } = route;

  const getStatus = computed(() => {
    const { status: routeStatus } = query;
    const { status } = props;
    return Number(routeStatus) || status;
  });

  const getMapValue = computed((): MapValue => {
    return unref(statusMapRef).get(unref(getStatus)) as MapValue;
  });

  unref(statusMapRef).set(ExceptionEnum.PAGE_NOT_ACCESS, {
    title: '403',
    status: `${ExceptionEnum.PAGE_NOT_ACCESS}`,
    subTitle: t('sys.exception.subTitle403'),
    btnText: t('sys.exception.backHome'),
  });

  unref(statusMapRef).set(ExceptionEnum.PAGE_NOT_FOUND, {
    title: '404',
    status: `${ExceptionEnum.PAGE_NOT_FOUND}`,
    subTitle: t('sys.exception.subTitle404'),
    btnText: t('sys.exception.backHome'),
  });

  unref(statusMapRef).set(ExceptionEnum.ERROR, {
    title: '500',
    status: `${ExceptionEnum.ERROR}`,
    subTitle: t('sys.exception.subTitle500'),
    btnText: t('sys.exception.backHome'),
  });

  unref(statusMapRef).set(ExceptionEnum.IP_LIMIT, {
    title: '403',
    status: `${ExceptionEnum.PAGE_NOT_ACCESS}`,
    subTitle: t('sys.exception.errMsg10101'),
    btnText: t('sys.exception.backHome'),
  });

  const { status: statusRes, title, subTitle, btnText } = unref(getMapValue) || {};

  const homePath = computed(() => {
    const packageName = path.split('/')[1];
    if (PackageEnum[packageName]) {
      return `/${PackageEnum[packageName]}`;
    }
    return '/';
  });
</script>

<style lang="less" scoped>
  @prefix-cls: ~'@{namespace}-main-box';

  .@{prefix-cls} {
    height: 100vh;
    width: 100vw;
    background-color: var(--component-bg-color);

    .title {
      color: var(--2-text, rgba(255, 255, 255, 0.24));

      /* 标题/加粗 64px */
      font-family: PingFang SC;
      font-size: 64px;
      font-style: normal;
      font-weight: 600;
      line-height: 72px; /* 112.5% */
    }

    .desc {
      color: var(--2-text, rgba(255, 255, 255, 0.24));

      /* 正文/常规 16px */
      font-family: PingFang SC;
      font-size: 16px;
      font-style: normal;
      font-weight: 400;
      line-height: 24px; /* 150% */
    }

    .btn {
      border-radius: var(--radius-radius, 4px);
      background: var(--5-hover, #23bfac);
      padding: 4px 12px;
      color: var(--fill-2, #f7f8fa);

      /* 正文/常规 12px */
      font-family: PingFang SC;
      font-size: 12px;
      font-style: normal;
      font-weight: 400;
      line-height: 20px; /* 166.667% */
    }
  }
</style>
